import React from 'react';
import {
	Document, Page,
} from '@react-pdf/renderer';
import { RequirementNode } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import {
	CourseEntity, CourseOfferingEntity, PlanSelectionEntity, ProgramRuleEntity, RequirementEntity,
	StudyPlanEntity,
} from 'Models/Entities';
import { DataIndex } from 'Util/DataIndexUtils';
import {
	DuplicateCourseItem,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';
import { sharedStyles } from 'Views/Pages/Pdf/styles';
import ProgramComponents from 'Views/Pages/Pdf/RequirementsFulfilmentCheck/ProgramComponents';
import { RequisiteWarning } from 'Queries/Hooks/StudyPlan/types';
import PdfBreak from 'Views/Pages/Pdf/Components/PDFBreak';
import DuplicateCourseSelection from 'Views/Pages/Pdf/RequirementsFulfilmentCheck/DuplicateCourseSelection';
import ProgramRules from 'Views/Pages/Pdf/RequirementsFulfilmentCheck/ProgramRules';
import UnitCompleted from 'Views/Pages/Pdf/Common/UnitCompleted';
import StructureUnitSelectedSummary from 'Views/Pages/Pdf/Common/StructureUnitSelectedSummary';
import Footer from 'Views/Pages/Pdf/Common/Footer';
import Header from 'Views/Pages/Pdf/Common/Header';
import 'Views/Pages/Pdf/fonts';
import EndOfDocument from 'Views/Pages/Pdf/Common/EndOfDocument';
import { capitalize } from 'lodash';

interface RequirementsFulfilmentCheckPdfProps {
	requirementTree: Record<string, RequirementNode>,
	studyPlan: StudyPlanEntity,
	programRules: ProgramRuleEntity[],
	planSelections: PlanSelectionEntity[],
	requirementsById: DataIndex<RequirementEntity>,
	coursesById: DataIndex<CourseEntity>,
	courseOfferingsById: DataIndex<CourseOfferingEntity>,
	requirementsByParentId: DataIndex<RequirementEntity[]>
	duplicateCourses: DuplicateCourseItem[],
	getCourseComponents: (courseId: string) => string[];
	completedCourseIds: string[],
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>,
	totalUnitsSelected: number,
}

const RequirementsFulfilmentCheckPdf = (props: RequirementsFulfilmentCheckPdfProps) => {
	const {
		requirementTree,
		studyPlan,
		programRules,
		planSelections,
		requirementsById,
		coursesById,
		courseOfferingsById,
		requirementsByParentId,
		duplicateCourses,
		getCourseComponents,
		completedCourseIds,
		requisiteWarningsByCourseId,
		totalUnitsSelected,
	} = props;

	return (
		<Document>
			<Page size="A4" style={sharedStyles.body}>
				<Header
					documentName={capitalize(window.lexicon.requirementsFulfilmentCheck)}
					studyPlan={studyPlan}
				/>

				<StructureUnitSelectedSummary
					requirementTree={requirementTree}
					studyPlan={studyPlan}
					coursesById={coursesById}
					duplicateCourses={duplicateCourses}
				/>

				<PdfBreak />

				<UnitCompleted
					studyPlan={studyPlan}
					coursesById={coursesById}
					completedCourseIds={completedCourseIds}
					getCourseComponents={courseId => getCourseComponents(courseId)}
					totalUnitsSelected={totalUnitsSelected}
				/>

				<PdfBreak />

				<ProgramRules
					programRules={programRules}
					coursesById={coursesById}
					planSelections={planSelections}
					completedCourseIds={completedCourseIds}
					description={studyPlan.programVersion.programRuleDescription}
					getCourseComponents={courseId => getCourseComponents(courseId)}
				/>

				<PdfBreak />

				<DuplicateCourseSelection
					description={studyPlan.programVersion.duplicateDescription}
					duplicateCourses={duplicateCourses}
					completedCourseIds={completedCourseIds}
				/>

				<PdfBreak />

				<ProgramComponents
					requirementsById={requirementsById}
					coursesById={coursesById}
					planSelections={planSelections}
					courseOfferingsById={courseOfferingsById}
					requirementsByParentId={requirementsByParentId}
					duplicateCourses={duplicateCourses}
					completedCourseIds={completedCourseIds}
					requisiteWarningsByCourseId={requisiteWarningsByCourseId}
				/>

				<PdfBreak />

				<EndOfDocument
					customDocumentName="Report"
				/>

				<Footer
					studyPlan={studyPlan}
				/>
			</Page>
		</Document>
	);
};

export default RequirementsFulfilmentCheckPdf;
